import { fabric } from 'fabric';
import { uniqueId } from 'lodash';
import { EditableLabel } from 'pages/easy-estimator/components/scrollable-area/design-countertop/drawer/editableLabel';
import { Subject } from 'rxjs';
import Vec2 from 'vec2';

import { calculateEdgeConstraints } from '@canvas-common';
import { CANVAS_OBJECT_TYPE_PREFIX, CORNER_RADIUS_SIGN } from '@common/constants';
import { CustomSideType, ShapeType, SideItemOption, SideItemType } from '@common/enums';
import { DrawingAppliance } from '@common/models/drawing-appliance.model';

import { EdgeModel, ISerializableDrawingShape, Skeleton, SkeletonSeg } from '.';

export interface IDrawingShape extends ISerializableDrawingShape {
    areaInfo?: AreaInfo;
    arrowsInfo?: ArrowInfo[];
    canvasObjectName?: string;
    editableLabels: EditableLabel[];
    establishedLetterStartIndex?: number;
    fabricGroup: fabric.Group;
    highlightPoints?: Vec2[];
    id: string;
    onTextChange?: Subject<string>;
    type: ShapeType;
}

/**
 * DrawingShape is a complete definition of a given shape in the design.
 * These are defined from a skeleton initially, from which the actual shape is generated
 * @property skeleton is the skeleton that defines the shape - it gets updated with rotate/translate
 * @property highlightPoints an array of points that defines the outline of the base shape as a polygon (does not represent rounded elements)
 * @property type the type of the shape
 */
export class DrawingShape implements IDrawingShape {
    appliances: DrawingAppliance[];
    areaInfo?: AreaInfo;
    arrowsInfo?: ArrowInfo[];
    canvasObjectName?: string;
    cornerMap: CornerMap;
    counterLabel?: number;
    drawingEdges: DrawingEdge[];
    edge?: EdgeModel;
    editableLabels: EditableLabel[];
    establishedLetterStartIndex?: number;
    fabricGroup: fabric.Group;
    highlightPoints?: Vec2[];
    id: string;
    label?: string;
    onTextChange?: Subject<string>;
    removedElementsHistory?: RemovedElementsHistory; // every edge that's not finished edge
    skeleton: Skeleton;
    type: ShapeType;

    /**
     *
     * @param props requires (at a minimum) skeleton and type
     */
    constructor(props: Partial<IDrawingShape> & { skeleton: Skeleton; type: ShapeType }) {
        // TODO: create real constructor from skeleton
        this.skeleton = props.skeleton; // required, no default
        this.cornerMap = props.cornerMap ?? {};
        this.drawingEdges = props.drawingEdges ?? [];
        this.type = props.type; //required, no default
        this.id = props.id ?? '';
        this.fabricGroup = props.fabricGroup ?? new fabric.Group();
        this.fabricGroup.set({
            subTargetCheck: true,
        });
        this.editableLabels = props.editableLabels ?? [];
        this.appliances = props.appliances ?? [];
        this.edge = props.edge ?? undefined;
    }

    get jointPointsCount(): number {
        // Ignores the first and last skeleton points
        return this.skeleton.length > 2 ? this.skeleton.length - 2 : 0;
    }

    /**
     * return a list of SimpleLines connecting all points of the highlightPoints
     * last point connects to first point
     *
     getPolygonLines(): Array<SimpleLine> | undefined {
     if (!this.highlightPoints) return;
     return MathFunctions.getLinesFromPolygonPoints(this.highlightPoints);
     }*/

    static cloneCornerMap(cornerMap: CornerMap): CornerMap {
        const clonedCornerMap = new CornerMap();

        for (const key of Object.keys(cornerMap)) {
            clonedCornerMap[key] = CornerInfo.clone(cornerMap[key]);
        }
        return clonedCornerMap;
    }

    static cloneCornerArray(cornerArray: CornerInfo[]): CornerInfo[] {
        return cornerArray.map((corner) => CornerInfo.clone(corner));
    }

    static cloneEdge(edge: DrawingEdge, copying: boolean, newShapeId: string, newEdgeId?: string): DrawingEdge {
        let edgeId = edge.id; // used for normal cloning
        if (copying) {
            if (newEdgeId) {
                edgeId = newEdgeId; // during copy passed in for used in edgesAndCorners
            } else {
                edgeId = uniqueId(); // during copy first run during shape copy
            }
        }
        return {
            ...edge,
            startDisplayVec: new Vec2(edge.startDisplayVec.x, edge.startDisplayVec.y),
            endDisplayVec: new Vec2(edge.endDisplayVec.x, edge.endDisplayVec.y),
            normalVec: new Vec2(edge.normalVec.x, edge.normalVec.y),
            dirVec: new Vec2(edge.dirVec.x, edge.dirVec.y),
            sideItem: SideItem.clone(edge.sideItem, edgeId),
            bumpOut: edge.bumpOut ? this.cloneBumpOut(edge.bumpOut) : undefined,
            id: edgeId,
            drawingShapeId: copying ? newShapeId : edge.drawingShapeId,
        };
    }

    static cloneAppliance(appliance: DrawingAppliance, copying: boolean): DrawingAppliance {
        return {
            ...appliance,
            centerPoint: new Vec2(appliance.centerPoint.x, appliance.centerPoint.y),
            id: copying ? uniqueId() : appliance.id,
        };
    }

    static cloneBumpOut(bumpOut: BumpOut): BumpOut {
        const clonedBump = Object.assign(new BumpOut(), bumpOut);
        clonedBump.arrowsInfo = bumpOut.arrowsInfo.map((arrow) => {
            return this.cloneArrowInfo(arrow);
        });
        return clonedBump;
    }

    static cloneArrowInfo(arrow: ArrowInfo): ArrowInfo {
        return {
            ...arrow,
            path: arrow.path.map((value) => {
                return new Vec2(value.x, value.y);
            }),
        };
    }

    static cloneEdgesAndCorners(
        shape: DrawingShape,
        copying: boolean,
        newShape: DrawingShape
    ): Array<RemovedEdgeAndCorners> | undefined {
        const removedEdgesAndCorners = shape.removedElementsHistory?.edgesAndCorners;
        if (!removedEdgesAndCorners) {
            return;
        }
        const clonedEdgesAndCorners: RemovedEdgeAndCorners[] = [];

        removedEdgesAndCorners.map((sideItem) => {
            const foundIndex = shape.drawingEdges.findIndex(
                (drawingEdge) => drawingEdge.id === sideItem.drawingEdge.id
            );
            const newEdgeId = newShape.drawingEdges[foundIndex].id;

            clonedEdgesAndCorners.push({
                ...sideItem,
                sideItemType: sideItem.sideItemType,
                drawingEdge: this.cloneEdge(sideItem.drawingEdge, copying, newShape.id, newEdgeId),
                arrowInfo: sideItem.arrowInfo ? this.cloneArrowInfo(sideItem.arrowInfo) : null,
                appliedCorners: this.cloneCornerArray(sideItem.appliedCorners),
            });
        });

        if (copying) {
            clonedEdgesAndCorners.forEach((newSideItem) => {
                if (newSideItem.arrowInfo) {
                    newSideItem.arrowInfo.drawingEdgeId = newSideItem.drawingEdge.id;
                }
            });
        }

        return clonedEdgesAndCorners;
    }

    static cloneSkeletonSegment(segment: SkeletonSeg): SkeletonSeg {
        return {
            ...segment,
            pos: new Vec2(segment.pos.x, segment.pos.y),
        };
    }

    /**
     * cloneAreaInfo is trivial but consistent with the other clone functions
     * @param area
     */
    static cloneAreaInfo(area: AreaInfo): AreaInfo {
        return {
            ...area,
        };
    }

    /**
     * cloneDrawingShape is used for making a copy of a shape when a user requests this, for
     * saving shapes in an undo/redo stack for those functions, and for creating a 'whatif'
     * version in memory to do recursive projections on edge sizes in order to find optimal
     * constraint values.
     * @param originalShape is the DrawingShape we will be cloning here
     * @param copying is passed down to more granular clone routines to control whether
     *          new unique id's need to be created for various objects.  This will be
     *          false for the case where we're cloning for the undo/redo stack.
     * @param skipConstraints is checked here to prevent an infinite recursive loop.
     *         If we’re cloning a shape to create another shape for the user,
     *         then we DO want to calculate the edge constraints for the new shape
     *         If we are cloning a shape just to have something in memory to do whatIf’s
     *         on, we’re already called the high level calculate edge constraints and
     *         need only refine them with the refine routine.
     */
    static cloneDrawingShape(originalShape: DrawingShape, copying: boolean, skipConstraints: boolean): DrawingShape {
        // this ensures the new object is a DrawingShape with all methods
        const clonedShape = Object.assign(new DrawingShape(originalShape), originalShape); // TODO Is the assign still necessary?  I don't think so.

        clonedShape.id = copying ? uniqueId() : originalShape.id;
        clonedShape.arrowsInfo = originalShape.arrowsInfo!.map((arrow) => {
            return this.cloneArrowInfo(arrow);
        });

        clonedShape.areaInfo = originalShape.areaInfo ? this.cloneAreaInfo(originalShape.areaInfo) : undefined;
        clonedShape.onTextChange = originalShape.onTextChange;
        clonedShape.cornerMap = this.cloneCornerMap(originalShape.cornerMap);
        clonedShape.drawingEdges = originalShape.drawingEdges.map((edge) => {
            return this.cloneEdge(edge, copying, clonedShape.id);
        });
        clonedShape.appliances = copying
            ? []
            : originalShape.appliances.map((appliance) => {
                  return this.cloneAppliance(appliance, copying);
              });
        clonedShape.skeleton = originalShape.skeleton.map((seg) => {
            return this.cloneSkeletonSegment(seg);
        });
        clonedShape.fabricGroup = copying ? new fabric.Group() : originalShape.fabricGroup;
        clonedShape.canvasObjectName = copying
            ? CANVAS_OBJECT_TYPE_PREFIX.SHAPE_MAIN + '_' + uniqueId()
            : originalShape.canvasObjectName;
        clonedShape.counterLabel = copying ? undefined : originalShape.counterLabel;
        clonedShape.label = copying ? undefined : originalShape.label;

        if (copying) {
            clonedShape.arrowsInfo.forEach((_arrow, index) => {
                if (clonedShape.arrowsInfo) {
                    clonedShape.arrowsInfo[index] = {
                        ...clonedShape.arrowsInfo[index],
                        drawingEdgeId: clonedShape?.drawingEdges[index]?.id,
                    };
                }
            });
            // this fixes duplicate shapes being unreachable with the mouse clicks
            clonedShape.fabricGroup.set({
                subTargetCheck: true,
            });
        }

        let clonedEdgesAndCorners;
        const clonedRemovedElementsHistory: RemovedElementsHistory = { edgesAndCorners: [] };
        if (originalShape.removedElementsHistory) {
            clonedEdgesAndCorners = this.cloneEdgesAndCorners(originalShape, copying, clonedShape);
            if (clonedEdgesAndCorners !== undefined) {
                clonedRemovedElementsHistory!.edgesAndCorners = clonedEdgesAndCorners;
            }
        }

        clonedShape.removedElementsHistory = clonedRemovedElementsHistory;
        clonedShape.establishedLetterStartIndex = originalShape.establishedLetterStartIndex;

        if (!skipConstraints) {
            calculateEdgeConstraints(clonedShape);
        }
        return clonedShape;
    }

    toSaved() {
        return {
            skeleton: this.skeleton,
            cornerMap: this.cornerMap,
            type: this.type,
            appliances: this.appliances,
            edge: this.edge,
        };
    }

    toJSON() {
        // onTextChange will result in cyclical errors
        // fabricGroup has no meaningful information to save

        return { ...this, onTextChange: undefined, fabricGroup: undefined };
    }

    darkenAllLabels() {
        this.editableLabels.forEach((editableLabel) => {
            editableLabel.darken();
        });
    }

    removeLabelLetters() {
        this.editableLabels.forEach((editableLabel) => {
            if (!editableLabel.labelText.text?.includes(CORNER_RADIUS_SIGN)) {
                editableLabel.labelText.text = '';
            }
        });
    }

    lightenAllLabels() {
        this.editableLabels.forEach((editableLabel) => {
            editableLabel.lighten();
        });
    }

    hideAllLabels() {
        this.editableLabels.forEach((editableLabel) => {
            editableLabel.hide();
        });
    }
}

export interface DrawingEdge {
    id: string;
    startCornerId: string;
    endCornerId: string;
    startDisplayVec: Vec2;
    endDisplayVec: Vec2;
    normalVec: Vec2;
    dirVec: Vec2;
    isVisibleInCanvas: boolean;
    drawingShapeId: string;
    sideItem: SideItem;
    customSideType?: CustomSideType;
    realRadius?: number;
    radiusSagitta?: number;
    bumpOut?: BumpOut;
}

export interface IMeasureInfo {
    label?: string;
    isVisibleInCanvas?: boolean;
    size?: number; //This is a radius in the case of CornerInfo and length in the case of ArrowInfo
}

export class MeasureInfo implements IMeasureInfo {
    label?: string;
    isVisibleInCanvas?: boolean;
    size?: number; //This is a radius in the case of CornerInfo and length in the case of ArrowInfo

    constructor({ label, isVisibleInCanvas, size }: IMeasureInfo) {
        this.label = label;
        this.isVisibleInCanvas = isVisibleInCanvas;
        this.size = size;
    }
}

export class CornerInfo extends MeasureInfo {
    id: string;
    labelText?: fabric.Text; // canvas text object reference to label
    labelMeasure?: fabric.IText; // canvas text object reference to radius measurement
    coords: Vec2;
    productId?: number;
    skIndex: number;
    angle?: number;
    isUpper: boolean; // top or bottom point against skeletonSeg
    radiusCenterPoint?: Vec2;
    orientationAngle?: number;
    hasPredefinedExtraEdging?: boolean;
    diagonalLength?: number;
    replacementStartCoords?: Vec2;
    replacementEndCoords?: Vec2;
    maxAllowedRadius?: number; //for constraints

    constructor(id: string, coords: Vec2, skIndex: number, isUpper: boolean) {
        super({});
        this.id = id;
        this.coords = coords;
        this.skIndex = skIndex;
        this.isUpper = isUpper;
    }

    // clone everything
    static clone(source: CornerInfo) {
        const clonedCorner = Object.assign(
            new CornerInfo(source.id, new Vec2(source.coords.x, source.coords.y), source.skIndex, source.isUpper),
            source
        );
        clonedCorner.labelText = fabric.util.object.clone(source.labelText);
        clonedCorner.labelMeasure = fabric.util.object.clone(source.labelMeasure);
        clonedCorner.radiusCenterPoint = source.radiusCenterPoint
            ? new Vec2(source.radiusCenterPoint.x, source.radiusCenterPoint.y)
            : undefined;
        clonedCorner.replacementStartCoords = source.replacementStartCoords
            ? new Vec2(source.replacementStartCoords.x, source.replacementStartCoords.y)
            : undefined;
        clonedCorner.replacementEndCoords = source.replacementEndCoords
            ? new Vec2(source.replacementEndCoords.x, source.replacementEndCoords.y)
            : undefined;
        return clonedCorner;
    }

    toJSON() {
        // just take the text of the fabric labels
        return { ...this, labelText: this.labelText?.text, labelMeasure: this.labelMeasure?.text };
    }
}

export interface CustomSideActionType {
    shape: DrawingShape;
    edge: DrawingEdge;
    disable: boolean;
}

export class CornerMap {
    [key: string]: CornerInfo;
}

export interface ArrowInfo extends MeasureInfo {
    path: Vec2[]; //which is equivalent to one item of the arrows in DrawingShape itself
    isInterior?: boolean;
    interiorSkIndex?: number;
    isDiagonalCorner?: boolean;
    drawingEdgeId?: string;
    isActiveForEdgeCalc?: boolean;
    minimumLength?: number;
    maximumLength?: number;
    isRadiusOut?: boolean;
    isBumpOut?: boolean;
    // can be backsplash or waterfall for now
    isSideItemPresent?: boolean;
    cornerId?: string;
}

export interface LineSegment {
    startPoint: Vec2;
    endPoint: Vec2;
}

export interface AreaInfo {
    main: number;
    diagCorner: number;
    total: number;

    [key: string]: number;

    //other types of area elements will come here
}

export interface RemovedElementsHistory {
    edgesAndCorners: Array<RemovedEdgeAndCorners>;
}

export interface RemovedEdgeAndCorners {
    sideItemType: SideItemType;
    drawingEdge: DrawingEdge;
    arrowInfo: ArrowInfo | null;
    appliedCorners: Array<CornerInfo>;
}

export class SideItem {
    depth: number;
    arrowsInfo: Array<ArrowInfo>;
    type?: SideItemOption;

    constructor(depth?: number, arrowsInfo?: Array<ArrowInfo>, type?: SideItemOption) {
        this.depth = depth || 0;
        this.arrowsInfo = arrowsInfo || [];
        this.type = type || undefined; // TODO EZE-1448
    }

    static clone(source: SideItem, edgeId?: string): SideItem {
        const newSideItem = Object.assign(new SideItem(), source);
        newSideItem.arrowsInfo = source.arrowsInfo.map((arrow) => {
            const newArrowInfo = DrawingShape.cloneArrowInfo(arrow);
            if (edgeId) {
                newArrowInfo.drawingEdgeId = edgeId;
            }
            return newArrowInfo;
        });

        return newSideItem;
    }
}

export class BumpOut {
    depth: number;
    length: number;
    counterClockwiseRemainderLength: number;
    clockwiseRemainderLength: number;
    arrowsInfo: Array<ArrowInfo>;

    constructor(
        depth?: number,
        length?: number,
        counterClockwiseRemainderLength?: number,
        clockwiseRemainderLength?: number
    ) {
        this.depth = depth || 0;
        this.length = length || 0;
        this.counterClockwiseRemainderLength = counterClockwiseRemainderLength || 0;
        this.clockwiseRemainderLength = clockwiseRemainderLength || 0;
        this.arrowsInfo = [];
    }
}
